// CandleAggregator.ts
// Aggregates tick data into OHLC candles for efficient storage and playback of distant time ranges.
// Supports multiple granularities (1m, 5m, 15m, 1h, etc.) with incremental updates.

import { TickData, CandleData, CandleSegment } from './SegmentSchema';
import { TQuote } from 'src/types';

export interface AggregationConfig {
    granularitySeconds: number; // Target candle duration in seconds
    symbol: string;
}

export interface CandleBucket {
    startEpoch: number;
    endEpoch: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    tickCount: number;
}

/**
 * CandleAggregator: Aggregates tick data into OHLC candles.
 * Supports streaming aggregation (add ticks incrementally) and batch aggregation.
 */
export class CandleAggregator {
    private _config: AggregationConfig;
    private _activeBucket: CandleBucket | null = null;

    constructor(config: AggregationConfig) {
        this._config = config;
    }

    /**
     * Aggregate an array of ticks into candles.
     * Returns array of completed candles (excludes incomplete final candle).
     */
    aggregateTicks(ticks: TickData[]): CandleData[] {
        if (ticks.length === 0) return [];

        const candles: CandleData[] = [];
        let currentBucket: CandleBucket | null = null;

        for (const tick of ticks) {
            const bucketStartEpoch = this._getBucketStart(tick.epoch);

            // Check if we need to start a new bucket
            if (!currentBucket || currentBucket.startEpoch !== bucketStartEpoch) {
                // Close previous bucket if exists
                if (currentBucket) {
                    candles.push(this._bucketToCandle(currentBucket));
                }

                // Start new bucket
                currentBucket = this._createBucket(bucketStartEpoch, tick);
            } else {
                // Update existing bucket
                this._updateBucket(currentBucket, tick);
            }
        }

        // Close final bucket if exists and is complete
        if (currentBucket && this._isBucketComplete(currentBucket)) {
            candles.push(this._bucketToCandle(currentBucket));
        }

        return candles;
    }

    /**
     * Add single tick to streaming aggregation.
     * Returns completed candle if bucket boundary crossed, null otherwise.
     */
    addTick(tick: TickData): CandleData | null {
        const bucketStartEpoch = this._getBucketStart(tick.epoch);

        // Check if we need to close current bucket and start new one
        if (this._activeBucket && this._activeBucket.startEpoch !== bucketStartEpoch) {
            const completedCandle = this._bucketToCandle(this._activeBucket);
            this._activeBucket = this._createBucket(bucketStartEpoch, tick);
            return completedCandle;
        }

        // Update or create bucket
        if (!this._activeBucket) {
            this._activeBucket = this._createBucket(bucketStartEpoch, tick);
        } else {
            this._updateBucket(this._activeBucket, tick);
        }

        return null;
    }

    /**
     * Flush active bucket (force close incomplete candle).
     */
    flush(): CandleData | null {
        if (!this._activeBucket) return null;

        const candle = this._bucketToCandle(this._activeBucket);
        this._activeBucket = null;
        return candle;
    }

    /**
     * Reset aggregator state.
     */
    reset(): void {
        this._activeBucket = null;
    }

    /**
     * Get active bucket (current incomplete candle).
     */
    getActiveBucket(): CandleBucket | null {
        return this._activeBucket;
    }

    /**
     * Aggregate quotes (TQuote format) into candles.
     */
    aggregateQuotes(quotes: TQuote[], granularitySeconds: number): CandleData[] {
        const ticks: TickData[] = quotes.map(q => ({
            epoch: q.DT ? q.DT.getTime() : Date.parse(`${q.Date}Z`),
            price: q.Close,
            volume: q.Volume ?? 1,
        }));

        const aggregator = new CandleAggregator({
            granularitySeconds,
            symbol: this._config.symbol,
        });

        return aggregator.aggregateTicks(ticks);
    }

    /**
     * Convert candles back to quotes for replay.
     */
    static candlesToQuotes(candles: CandleData[]): TQuote[] {
        return candles.map(candle => ({
            Date: new Date(candle.epoch).toISOString().substring(0, 19),
            DT: new Date(candle.epoch),
            Open: candle.open,
            High: candle.high,
            Low: candle.low,
            Close: candle.close,
            Volume: candle.volume,
        }));
    }

    /**
     * Create candle segment from aggregated candles.
     */
    static createCandleSegment(
        candles: CandleData[],
        symbol: string,
        granularity: number
    ): CandleSegment {
        if (candles.length === 0) {
            throw new Error('Cannot create segment from empty candles array');
        }

        const startEpoch = candles[0].epoch;
        const endEpoch = candles[candles.length - 1].epoch;
        const segmentId = `${symbol}-${granularity}-${startEpoch}`;

        return {
            metadata: {
                segmentId,
                symbol,
                granularity,
                type: 'candle',
                startEpoch,
                endEpoch,
                count: candles.length,
                sizeBytes: JSON.stringify(candles).length,
                compression: 'none',
                createdAt: Date.now(),
            },
            candles,
        };
    }

    // Private methods

    private _getBucketStart(epoch: number): number {
        const granularityMs = this._config.granularitySeconds * 1000;
        return Math.floor(epoch / granularityMs) * granularityMs;
    }

    private _createBucket(startEpoch: number, tick: TickData): CandleBucket {
        const granularityMs = this._config.granularitySeconds * 1000;
        return {
            startEpoch,
            endEpoch: startEpoch + granularityMs,
            open: tick.price,
            high: tick.price,
            low: tick.price,
            close: tick.price,
            volume: tick.volume ?? 1,
            tickCount: 1,
        };
    }

    private _updateBucket(bucket: CandleBucket, tick: TickData): void {
        bucket.high = Math.max(bucket.high, tick.price);
        bucket.low = Math.min(bucket.low, tick.price);
        bucket.close = tick.price;
        bucket.volume += (tick.volume ?? 1);
        bucket.tickCount++;
    }

    private _bucketToCandle(bucket: CandleBucket): CandleData {
        return {
            epoch: bucket.startEpoch,
            open: bucket.open,
            high: bucket.high,
            low: bucket.low,
            close: bucket.close,
            volume: bucket.volume,
        };
    }

    private _isBucketComplete(bucket: CandleBucket): boolean {
        const now = Date.now();
        return now >= bucket.endEpoch;
    }
}

/**
 * Granularity presets for common timeframes.
 */
export const GRANULARITY_PRESETS = {
    TICK: 0,
    ONE_MINUTE: 60,
    FIVE_MINUTES: 5 * 60,
    FIFTEEN_MINUTES: 15 * 60,
    THIRTY_MINUTES: 30 * 60,
    ONE_HOUR: 60 * 60,
    FOUR_HOURS: 4 * 60 * 60,
    ONE_DAY: 24 * 60 * 60,
} as const;

/**
 * Determine appropriate granularity based on time range.
 * Recent data uses ticks, distant data uses progressively coarser candles.
 */
export function selectGranularityForRange(
    _startEpoch: number,
    endEpoch: number,
    currentEpoch: number = Date.now()
): number {
    const distanceFromNowMs = currentEpoch - endEpoch;

    // Recent data (within last 7 days): use ticks
    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
    if (distanceFromNowMs <= SEVEN_DAYS_MS) {
        return GRANULARITY_PRESETS.TICK;
    }

    // 7-30 days old: use 1-minute candles
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    if (distanceFromNowMs <= THIRTY_DAYS_MS) {
        return GRANULARITY_PRESETS.ONE_MINUTE;
    }

    // 30-90 days old: use 5-minute candles
    const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
    if (distanceFromNowMs <= NINETY_DAYS_MS) {
        return GRANULARITY_PRESETS.FIVE_MINUTES;
    }

    // 90-180 days old: use 15-minute candles
    const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
    if (distanceFromNowMs <= ONE_EIGHTY_DAYS_MS) {
        return GRANULARITY_PRESETS.FIFTEEN_MINUTES;
    }

    // 180-365 days old: use 1-hour candles
    return GRANULARITY_PRESETS.ONE_HOUR;
}

/**
 * Batch aggregate multiple tick segments into candle segments.
 */
export async function batchAggregateSegments(
    tickSegments: { ticks: TickData[]; symbol: string }[],
    targetGranularity: number
): Promise<CandleSegment[]> {
    const candleSegments: CandleSegment[] = [];

    for (const { ticks, symbol } of tickSegments) {
        if (ticks.length === 0) continue;

        const aggregator = new CandleAggregator({
            granularitySeconds: targetGranularity,
            symbol,
        });

        const candles = aggregator.aggregateTicks(ticks);
        
        if (candles.length > 0) {
            const segment = CandleAggregator.createCandleSegment(
                candles,
                symbol,
                targetGranularity
            );
            candleSegments.push(segment);
        }
    }

    return candleSegments;
}
